// Mobile viewport plumbing.
//
// Two different heights matter on a phone and conflating them is what put the
// composer behind the keyboard:
//
//   * the *layout* viewport (window.innerHeight) — what position:fixed is laid
//     out against, and what the URL bar collapsing changes. On iOS this does
//     NOT shrink when the keyboard opens.
//   * the *visual* viewport (window.visualViewport) — what the user can
//     actually see. This is the one the keyboard shrinks.
//
// The old code only had innerHeight, so it guessed at the keyboard with a
// height-ratio heuristic and then bailed out of resizing entirely. That left a
// composer pinned to `bottom: 0` of a viewport whose bottom was underneath the
// keyboard. visualViewport gives us the real number, so we publish it and let
// CSS position against it.
//
// Published on <html>:
//   --app-height       height of the visible area. With the keyboard up this is
//                      visualViewport.height; otherwise the layout viewport.
//   --vv-top           where that visible area starts inside the layout
//                      viewport (visualViewport.offsetTop while the keyboard is
//                      up, else 0). The touch shell is position:fixed at this
//                      top, so shell = exactly the rectangle the user can see.
//   --vh               1% of --app-height, for legacy `calc(var(--vh) * n)`
//   .keyboard-open     class, set while the keyboard is up
//
// Anything that must sit on the keyboard edge (the chat composer) is placed
// inside the shell rather than position:fixed with a bottom inset: the shell
// is the visible rectangle, and the layout viewport a fixed element would
// hang off is not something iOS reports consistently (see below).
//
// Why the shell is fixed rather than in flow: to reveal the focused field iOS
// scrolls the *document* (window.scrollY) as well as panning the visual
// viewport (offsetTop), even with overflow:hidden on html/body. The old
// in-flow shell was sized to height + offsetTop but still sat at document
// y=0, so every px of scrollY cut the header off the top and showed the same
// px of bare <body> between the shell and the keyboard.

// How the keyboard is detected. The first version took "layout height minus
// visible area" from window.innerHeight, assuming iOS leaves innerHeight alone.
// iOS 26 Safari does not: when it scrolls to reveal the field it also shrinks
// innerHeight by the same amount (measured on an iPhone: innerHeight 699 ->
// 446 with scrollY 253 and offsetTop 253), the difference went negative, the
// keyboard read as closed, and the full-height shell stayed at the top with
// its upper part scrolled off and a blank band above the keyboard. So the
// keyboard is now "the visible height fell while a text field has focus":
// the full height is re-measured whenever nothing is being typed into (URL
// bar, rotation) and held while something is.
//
// Small drops are browser chrome settling (URL bar animating), not a
// keyboard. Treating those as a keyboard makes the composer jitter.
const KEYBOARD_MIN_INSET = 80;

const NON_TEXT_INPUTS = new Set([
  'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
]);

function isTyping(el) {
  if (!el) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
  return el.tagName === 'INPUT' && !NON_TEXT_INPUTS.has(String(el.type || 'text').toLowerCase());
}

export function installMobileViewport() {
  const root = document.documentElement;
  const viewport = window.visualViewport || null;
  let wasKeyboardOpen = false;
  let fullHeight = 0;

  const apply = () => {
    const layoutHeight = window.innerHeight;

    // Without visualViewport (older Android WebViews) we cannot separate a
    // keyboard from a URL-bar collapse, so report no inset and keep the old
    // full-height behaviour rather than guessing wrong. Pinch-zoom also
    // shrinks the visual viewport; following it would re-lay the app out into
    // the zoomed region and undo the zoom, so a zoomed page is treated as
    // keyboard-down too.
    const tracking = Boolean(viewport) && Math.abs(viewport.scale - 1) < 0.01;
    const visualHeight = tracking ? viewport.height : layoutHeight;
    const offsetTop = tracking ? Math.max(0, viewport.offsetTop) : 0;

    if (!fullHeight || !isTyping(document.activeElement)) fullHeight = visualHeight;
    const keyboardHeight = Math.max(0, Math.round(fullHeight - visualHeight));
    const keyboardOpen = tracking && keyboardHeight >= KEYBOARD_MIN_INSET;

    const top = keyboardOpen ? Math.round(offsetTop) : 0;
    const usableHeight = keyboardOpen ? Math.round(visualHeight) : Math.round(layoutHeight);

    root.style.setProperty('--vv-top', `${top}px`);
    root.style.setProperty('--app-height', `${usableHeight}px`);
    root.style.setProperty('--vh', `${usableHeight / 100}px`);
    root.classList.toggle('keyboard-open', keyboardOpen);

    // html/body are overflow:hidden, so the user cannot scroll back a document
    // scroll iOS leaves behind after the keyboard closes (iOS 26 can also leave
    // visualViewport.offsetTop stuck). Snap it home on the close transition.
    if (wasKeyboardOpen && !keyboardOpen) window.scrollTo(0, 0);
    wasKeyboardOpen = keyboardOpen;
  };

  // visualViewport events fire per animation frame while the keyboard slides
  // in. Coalescing to one rAF keeps the composer glued to the keyboard edge
  // instead of trailing it, which a debounce would cause.
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      apply();
    });
  };

  apply();

  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  window.addEventListener('scroll', schedule, { passive: true });
  if (viewport) {
    viewport.addEventListener('resize', schedule);
    viewport.addEventListener('scroll', schedule);
  }

  return () => {
    if (frame) window.cancelAnimationFrame(frame);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    window.removeEventListener('scroll', schedule);
    if (viewport) {
      viewport.removeEventListener('resize', schedule);
      viewport.removeEventListener('scroll', schedule);
    }
    root.classList.remove('keyboard-open');
  };
}
